import {
  selectSetError,
  selectSetFetchedUserResponsesForEuIds,
  selectSetLoading,
  selectSetLocalUserResponses,
  useUserResponseStore,
} from '@/pages/userResponses/store/userResponseStore';
import { useList } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { useEffect, useMemo } from 'react';

/**
 * Props interface for the useHydrateDBUserResponses hook
 * Defines all possible filter parameters for fetching user responses from the database
 */
export interface HydrateDBUserResponsesHookProps {
  experienceId?: number; // Filter by specific experience ID
  userId?: number; // Filter by specific user ID
  experienceUserId?: number; // Filter by specific experience-user relationship ID
  segmentId?: number; // Filter by specific segment/question ID
  userIds?: number[]; // Filter by multiple user IDs
  experienceUserIds?: number[]; // Filter by multiple experience-user relationship IDs
  withAnnotations?: boolean; // Include annotation data in the response
  serialized?: boolean; // Return serialized/formatted data
  shouldFetch?: boolean; // Control whether the hook should actually fetch data
}

/**
 * Custom hook for hydrating (loading) user responses from the database
 * This hook fetches user responses based on various filter criteria and updates the local store
 *
 * Purpose:
 * - Fetch user responses from the database API
 * - Store the fetched data in the local Zustand store
 * - Provide loading states and error handling
 * - Allow manual refetching of data
 *
 * @param props - Filter parameters and options for fetching user responses
 * @returns Object containing loading states, data, and control functions
 */
export const useHydrateDBUserResponses = (props: HydrateDBUserResponsesHookProps) => {
  console.log('useHydrateDBUserResponses props ==> ', props);

  // Extract experienceUserIds from props with default empty array
  const { experienceUserIds = [] } = props;
  const setLocalUserResponses = useUserResponseStore(selectSetLocalUserResponses);
  const setLoading = useUserResponseStore(selectSetLoading);
  const setError = useUserResponseStore(selectSetError);
  const setFetchedUserResponsesForEuIds = useUserResponseStore(
    selectSetFetchedUserResponsesForEuIds
  );

  /**
   * Build filters for the useList hook based on provided props
   * Converts props into the format expected by the API
   *
   * This memoized value prevents unnecessary re-renders when props haven't changed
   */
  const filters = useMemo(() => {
    const params: Record<string, any> = {};

    // Add filter parameters based on what's provided in props
    if (experienceUserIds?.length) {
      params.by_eu_ids = experienceUserIds; // Filter by experience-user IDs
    }
    if (props.experienceId) params.by_experience_id = props.experienceId; // Filter by experience ID
    if (props.userId) params.by_user_id = props.userId; // Filter by user ID
    if (props.experienceUserId) params.by_experience_user_id = props.experienceUserId; // Filter by experience-user ID
    if (props.userIds?.length) params.by_u_ids = props.userIds; // Filter by multiple user IDs
    if (props.segmentId) params.by_segment_id = props.segmentId; // Filter by segment ID

    // Set default options for data format
    params.with_annotations = props.withAnnotations ?? true; // Include annotations by default
    params.serialized = props.serialized ?? true; // Return serialized data by default

    // Convert params object to filter array format expected by Refine
    return Object.entries(params).map(([key, value]) => ({
      field: key,
      operator: 'eq' as const,
      value,
    }));
  }, [
    props.userId,
    props.experienceUserId,
    props.userIds,
    props.segmentId,
    props.withAnnotations,
    props.serialized,
    experienceUserIds,
  ]);

  /**
   * Use Refine's useList hook to fetch user responses from the API
   * This hook handles the actual HTTP request and provides loading states
   */
  const {
    data: userResponsesData, // The fetched data
    isLoading, // Loading state
    isError, // Error state
    error, // Error details
    isRefetching, // Refetching state
    refetch, // Function to manually refetch data
  } = useList({
    resource: 'user_responses', // API endpoint resource name
    filters: filters, // Filter parameters for the query
    pagination: {
      pageSize: 600, // Fetch up to 300 records per request
    },
    queryOptions: {
      enabled: props.shouldFetch, // Only fetch if shouldFetch is true
      cacheTime: 0, // Set cache time to 0 - Make sure it should not request BE continuously for any case - currently we are controlling this through shouldFetch
      onSuccess: (data) => {
        // Callback executed when data is successfully fetched
        console.log(
          'VirtualizedExperienceUsersTable ==> user_responses list success data ==> ',
          data
        );
        if (data?.data) {
          // Update the local store with fetched user responses
          setLocalUserResponses(data.data);
          // Track which experience user IDs have been fetched
          setFetchedUserResponsesForEuIds(experienceUserIds);
        }
      },
      onError: (error) => {
        // Callback executed when an error occurs
        setError(error?.message || 'Failed to fetch user responses');
        Sentry.captureException(new Error(error?.message ?? 'hydrateDBUserResponses failed'), {
          extra: {
            op: 'hydrateDBUserResponses',
            status: (error as any)?.statusCode ?? (error as any)?.response?.status,
          },
        });
      },
    },
    meta: {
      endpoint: 'user_responses.json', // Specific API endpoint
    },
  });

  /**
   * Sync the global store loading and error states with the query states
   * This ensures the store always reflects the current fetch status
   */
  useEffect(() => {
    setLoading(isLoading); // Update store loading state
    if (!isError) {
      setError(null); // Clear error if no error exists
    }
  }, [isLoading, isError, setLoading, setError]);

  // Alternative fetch method (commented out)
  // const fetchUserResponses = useCallback(() => {
  //   refetch();
  // }, [refetch]);

  // Return the hook's public interface
  return {
    isLoading, // Current loading state
    isRefetching, // Current refetching state
    isError, // Current error state
    error, // Error details
    userResponses: userResponsesData?.data || [], // Fetched user responses array
    refetch, // Function to manually refetch data
  };
};
